import React, { useState } from 'react'
import { Box, Button, ButtonBase, Typography } from '@mui/material'

import { AppBrandMark, AskIrisButton } from '@components'
import { dsColorNew } from '@palette'
import { addDays, fmtDay, fmtWeekday, fromIso } from '../../../@services/dates'
import { money, ordinal, signedMoney, signedPct } from '../../../@services/format'
import { CARD_ROWS, capLast7, capMoves } from '../@services/cards'
import { cardEvidence } from '../../../@services/askEvidence'


export const cardSx = { backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '16px', boxShadow: '0 1px 2px rgba(12,26,62,.04)', p: 2, minWidth: 0 }
const titleSx = { fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }
const subSx = { fontSize: 12, color: dsColorNew.textMuted, mt: 0.25 }
const pillSx = { display: 'inline-block', px: 1, py: '1px', borderRadius: '999px', fontSize: 11, fontWeight: 600, backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }
const Thin = () => <Box component="span" title="Fewer than 5 prices that day" sx={{ color: dsColorNew.noteAmber, fontWeight: 700, ml: 0.5 }}>!</Box>
// the "!" is explained on the card itself, not only on hover (touch screens; the mock's foot lines)
const ThinFoot = ({ children }) => <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1.25 }}>
	<Box component="span" sx={{ color: dsColorNew.noteAmber, fontWeight: 700, mr: 0.5 }}>!</Box>{children}
</Typography>

/**
 * The card's title with, for "Ask Iris" (CU-869fkdhjv phase 3, UX C9), its icon in the top-right corner. `ask` =
 * `{ askable, officeName, lor, carsText, marketText, scope }` from the page, `evidence` what the card shows; without
 * `ask` (or under "Updating…") the title is as before.
 */
function CardTitle({ title, ask, evidence }) {
	if (!ask?.askable) return <Typography sx={titleSx}>{title}</Typography>
	return <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
		<Typography sx={{ ...titleSx, flex: 1, minWidth: 0 }}>{title}</Typography>
		<AskIrisButton variant="icon" tooltip={`Ask Iris about ${title}`} getAsk={() => cardEvidence({ title, ...ask, ...evidence })} sx={{ my: -0.5, mr: -0.5 }}/>
	</Box>
}

/** ▼ green = cheaper / down; ▲ red = dearer / up; always with the sign (Curro's decision 1 of CU-869fmhmx8) */
export function Change({ eur, pct, code, size = 13 }) {
	if (eur === null || eur === undefined) return null
	const up = eur > 0, flat = Math.round(eur) === 0
	const color = flat ? dsColorNew.textMuted : up ? dsColorNew.dangerRed : dsColorNew.successText
	return <Box component="span" sx={{ color, fontWeight: 600, fontSize: size, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
		{flat ? '=' : up ? '▲' : '▼'} {signedPct(pct)} {signedMoney(eur, code)}
	</Box>
}

const captureOf = (curve, d) => {
	const pickup = fromIso(curve.pickupDates?.[0])
	return pickup ? addDays(pickup, -d) : null
}

/** CA 10: each provider's change over the last 7 days of capture (before pick-up when it has gone by) */
const MoreButton = ({ open, hidden, onClick }) => (hidden || open ? <Button variant="text" size="small" onClick={onClick}
	sx={{ textTransform: 'none', alignSelf: 'flex-start', px: 0.5, mt: 0.5, fontSize: 12.5 }}>{open ? 'Show fewer' : `Show all (${hidden} more)`}</Button> : null)

export function Last7Card({ rows: all, curve, code, past, onOpen, ask = null }) {
	const [open, setOpen] = useState(false)
	const capped = capLast7(all)
	const rows = open ? all : capped.rows
	const anyNow = rows.find(r => r.now)
	const until = anyNow ? captureOf(curve, Math.min(...rows.filter(r => r.now).map(r => r.now.d))) : null
	return <Box component="section" aria-label="Last 7 days" sx={cardSx}>
		<CardTitle title={past ? 'Last 7 days before pick-up' : 'Last 7 days'} ask={ask} evidence={{ card: 'last7', rows, curve, code }}/>
		<Typography sx={subSx}>{until && curve.single ? `${fmtDay(addDays(until, -7), false)} → ${fmtDay(until, false)} (last capture) · ` : ''}{open || !capped.hidden ? 'biggest drop first' : `the ${CARD_ROWS} biggest changes${curve.you ? ' and you' : ''}, biggest drop first`}</Typography>
		<Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
			{rows.map(r => <ButtonBase key={r.line.key} onClick={() => onOpen(r.line.providerId)} sx={{
				display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6, borderRadius: '8px', justifyContent: 'flex-start', textAlign: 'left',
				backgroundColor: r.line.role === 'you' ? dsColorNew.pillActiveBg : 'transparent', '&:hover': { backgroundColor: dsColorNew.hoverBgLight },
			}}>
				<AppBrandMark name={r.line.name} size={16}/>
				<Box component="span" sx={{ fontSize: 13, color: dsColorNew.textDark, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{r.line.name}{r.line.tag ? <Box component="span" sx={{ ...pillSx, ml: 0.75 }}>{r.line.tag}</Box> : null}
				</Box>
				{r.none
					? <Box component="span" sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>{r.noPrice ? 'no prices' : 'no price 7 days before'}</Box>
					: <><Change eur={r.eur} pct={r.pct} code={code}/>{r.thin && <Thin/>}</>}
			</ButtonBase>)}
			{!rows.length && <Typography sx={{ fontSize: 13, color: dsColorNew.textSubtle }}>No provider shown.</Typography>}
			<MoreButton open={open} hidden={capped.hidden} onClick={() => setOpen(v => !v)}/>
		</Box>
		{rows.some(r => r.thin) && <ThinFoot>fewer than 5 prices on one of the two days</ThinFoot>}
	</Box>
}

const moveText = (m, curve, code) => {
	const when = curve.single
		? `on ${fmtWeekday(captureOf(curve, m.d), false)}, ${m.d} day${m.d === 1 ? '' : 's'} before pick-up`
		: `${m.d} day${m.d === 1 ? '' : 's'} before each pick-up`
	return <>{signedPct(m.pct)} · {signedMoney(m.eur, code)} {when}</>
}

/** CA 11: the first to move 5 % or more against its previous capture day, and who followed in the next 7 days */
export function WhoMovedFirstCard({ moved: all, curve, code, onOpen, ask = null }) {
	const [open, setOpen] = useState(false)
	const capped = capMoves(all)
	const moved = open ? all : capped
	const nobody = !moved.first.length
	return <Box component="section" aria-label="Who moved first" sx={cardSx}>
		<CardTitle title="Who moved first" ask={ask} evidence={{ card: 'whoMovedFirst', moved, curve, code }}/>
		<Typography sx={subSx}>in the {curve.days} days before pick-up</Typography>
		<Box sx={{ mt: 1 }}><Box component="span" sx={pillSx}>moved 5 % or more vs its previous capture day</Box></Box>
		{nobody
			? <Box sx={{ mt: 1.5 }}>
				<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textDark }}>Nobody moved 5 % or more</Typography>
				{!!moved.biggest && <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 0.5 }}>
					Biggest daily move: {moved.biggest.line.name} {moveText(moved.biggest, curve, code)}
				</Typography>}
			</Box>
			: <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
				{moved.first.map(m => <ButtonBase key={m.line.key} onClick={() => onOpen(m.line.providerId)} sx={{ justifyContent: 'flex-start', textAlign: 'left', borderRadius: '8px', p: 0.5 }}>
					<Box>
						<Typography sx={{ fontSize: 15, fontWeight: 700, color: dsColorNew.textTitle, display: 'flex', alignItems: 'center', gap: 0.75 }}>
							<AppBrandMark name={m.line.name} size={18}/>{m.line.name}{m.thin && <Thin/>}
						</Typography>
						<Typography sx={{ fontSize: 13, color: m.eur > 0 ? dsColorNew.dangerRed : dsColorNew.successText, fontWeight: 600 }}>
							{m.eur > 0 ? '▲' : '▼'} {moveText(m, curve, code)}
						</Typography>
					</Box>
				</ButtonBase>)}
				<Box sx={{ borderTop: `1px solid ${dsColorNew.grayBorder}`, pt: 1 }}>
					<Typography sx={{ fontSize: 12, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6 }}>
						{all.followers.length ? `Followed in the next 7 days${all.followers.length > moved.followers.length ? ` (${moved.followers.length} of ${all.followers.length})` : ''}` : 'Nobody followed in the next 7 days'}
					</Typography>
					{moved.followers.map(f => <Box key={f.line.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 13, mt: 0.5 }}>
						<AppBrandMark name={f.line.name} size={14}/><span>{f.line.name}</span>{f.thin && <Thin/>}<Box sx={{ flex: 1 }}/><Change eur={f.eur} pct={f.pct} code={code} size={12}/>
					</Box>)}
					<MoreButton open={open} hidden={capped.hidden} onClick={() => setOpen(v => !v)}/>
				</Box>
			</Box>}
		{[...moved.first, ...moved.followers, ...(nobody && moved.biggest ? [moved.biggest] : [])].some(m => m.thin) && <ThinFoot>a day with fewer than 5 prices</ThinFoot>}
	</Box>
}

/** CA 12: your rank on the last capture day and the gap to the cheapest (or below the second); CA 17 without a brand */
export function PositionCard({ pos, code, past, identity, onSettings, curve = null, ask = null }) {
	if (!identity) return <Box component="section" aria-label="Your position" sx={{ ...cardSx, borderStyle: 'dashed', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.25 }}>
		<Typography sx={{ fontSize: 14, color: dsColorNew.textDark }}>Set your brand in <b>Settings › Account</b> to see your row and your position.</Typography>
		<Box><Button variant="outlined" size="small" onClick={onSettings} sx={{ textTransform: 'none' }}>Go to Settings › Account</Button></Box>
	</Box>
	const title = past ? 'Your position at pick-up' : 'Your position today'
	return <Box component="section" aria-label={title} sx={cardSx}>
		<CardTitle title={title} ask={ask} evidence={{ card: 'position', pos, identity, curve, code }}/>
		{!pos || pos.none ? <Typography sx={{ fontSize: 13, color: dsColorNew.textSubtle, mt: 1.5 }}>No prices of your brand in this curve.</Typography>
			: pos.alone ? <Typography sx={{ fontSize: 13, color: dsColorNew.textSubtle, mt: 1.5 }}>No competitor with a price that day.</Typography>
				: <>
					<Typography sx={subSx}>{pos.d === 0 ? 'on pick-up day' : `${pos.d} day${pos.d === 1 ? '' : 's'} before pick-up`} · 1 = the cheapest</Typography>
					<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1.25 }}>
						<Box component="span" sx={{
							px: 1.25, py: 0.25, borderRadius: '999px', fontSize: 18, fontWeight: 700,
							backgroundColor: pos.rank === 1 ? dsColorNew.successBg || '#DCFCE7' : dsColorNew.pillTrackBg, color: pos.rank === 1 ? dsColorNew.successText : dsColorNew.textTitle,
						}}>{ordinal(pos.rank)}</Box>
						<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>of {pos.of}{pos.thin && <Thin/>}</Typography>
						<Box sx={{ flex: 1 }}/>
						<Typography sx={{ fontSize: 15, fontWeight: 700, color: dsColorNew.textTitle }}>{money(pos.you, code)}</Typography>
					</Box>
					<Typography sx={{ fontSize: 13, mt: 1, color: pos.gap.kind === 'below' ? dsColorNew.successText : dsColorNew.dangerRed, fontWeight: 600 }}>
						{money(pos.gap.eur, code)} ({signedPct(pos.gap.kind === 'below' ? -pos.gap.pct : pos.gap.pct)}) {pos.gap.kind} {pos.gap.name}, {pos.gap.kind === 'below' ? 'the second' : 'the cheapest'}
					</Typography>
					{pos.median !== null && <Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.5 }}>
						{money(Math.abs(pos.you - pos.median), code)} {pos.you >= pos.median ? 'above' : 'below'} Market median ({money(pos.median, code)})
					</Typography>}
					{!!pos.noPrice && <Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mt: 0.5 }}>{pos.noPrice} provider{pos.noPrice === 1 ? '' : 's'} without a price that day</Typography>}
					{pos.thin && <ThinFoot>fewer than 5 prices that day</ThinFoot>}
				</>}
	</Box>
}
